(() => {
  const variantMatches = (variant, selection, context = {}) => {
    if (selection.size && variant.size !== selection.size) return false;
    if (selection.color && variant.color !== selection.color) return false;
    if (context.availability !== null && context.availability !== undefined && variant.available !== context.availability) return false;
    if ((context.availability === null || context.availability === undefined) && (selection.size || selection.color) && !variant.available) return false;
    if (context.minPrice !== null && context.minPrice !== undefined && variant.price < context.minPrice) return false;
    if (context.maxPrice !== null && context.maxPrice !== undefined && variant.price > context.maxPrice) return false;
    return true;
  };

  const contextFromUrl = (url) => {
    const params = new URL(url).searchParams;
    const stockValues = params.getAll('filter.v.availability').flatMap(value => value.split(','));
    const hasAvailable = stockValues.includes('1');
    const hasUnavailable = stockValues.includes('0');
    const price = (name) => {
      const value = params.get(name);
      if (value === null || value.trim() === '') return null;
      const amount = Number(value);
      return Number.isFinite(amount) ? Math.round(amount * 100) : null;
    };
    return {
      availability: hasAvailable === hasUnavailable ? null : hasAvailable,
      minPrice: price('filter.v.price.gte'),
      maxPrice: price('filter.v.price.lte')
    };
  };

  const selectionFromUrl = (url) => {
    const params = new URL(url).searchParams;
    return { size: params.get('li_size') || '', color: params.get('li_color') || '' };
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { variantMatches, contextFromUrl, selectionFromUrl };
  }
  if (typeof document === 'undefined' || customElements.get('li-ux-collection-filters')) return;

  const state = { selection: selectionFromUrl(window.location.href), components: new Set() };

  const updateUrl = (push) => {
    const url = new URL(window.location.href);
    for (const key of ['size', 'color']) {
      const name = 'li_' + key;
      if (state.selection[key]) url.searchParams.set(name, state.selection[key]);
      else url.searchParams.delete(name);
    }
    const next = url.pathname + url.search + url.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (next === current) return;
    const historyState = Object.assign({}, window.history.state, { searchParams: url.search.slice(1) });
    window.history[push ? 'pushState' : 'replaceState'](historyState, '', next);
  };

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.container = this.closest('#ProductGridContainer');
      if (!this.container) return;
      try {
        this.products = Array.from(this.container.querySelectorAll('#product-grid > [data-li-ux-product]')).map(element => ({
          element,
          variants: JSON.parse(element.dataset.liUxVariants)
        }));
      } catch {
        return;
      }
      this.fields = {
        size: this.querySelector('[data-li-ux-field="size"]'),
        color: this.querySelector('[data-li-ux-field="color"]')
      };
      this.options = {};
      for (const key of ['size', 'color']) {
        const values = Array.from(new Set(this.products.flatMap(product => product.variants.map(variant => variant[key])).filter(Boolean)));
        values.sort((a, b) => {
          if (key === 'size') {
            const order = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
            const first = order.indexOf(a.toUpperCase());
            const second = order.indexOf(b.toUpperCase());
            if (first !== -1 && second !== -1) return first - second;
          }
          return a.localeCompare(b, document.documentElement.lang || 'ro', { numeric: true });
        });
        this.options[key] = values;
        this.querySelector('[data-li-ux-field="' + key + '"]').hidden = values.length === 0;
      }
      if (!this.options.size.length && !this.options.color.length) return;
      state.components.add(this);
      this.addEventListener('click', (event) => {
        const option = event.target.closest('[data-li-ux-option]');
        if (!option || option.disabled) return;
        const field = option.closest('[data-li-ux-field]');
        state.selection = Object.assign({}, state.selection, { [field.dataset.liUxField]: option.dataset.liUxOption });
        updateUrl(true);
        this.apply();
        this.closeFields();
        field.querySelector('summary').focus();
      }, { signal: this.controller.signal });
      this.querySelector('[data-li-ux-reset]').addEventListener('click', () => {
        state.selection = { size: '', color: '' };
        updateUrl(true);
        this.apply();
        this.closeFields();
        Object.values(this.fields).find(field => !field.hidden)?.querySelector('summary').focus();
      }, { signal: this.controller.signal });
      for (const field of Object.values(this.fields)) {
        field.addEventListener('toggle', () => {
          if (field.open) this.closeFields(field);
        }, { signal: this.controller.signal });
      }
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.controller.signal });
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.closeFields();
      }, { signal: this.controller.signal });
      document.addEventListener('focusin', (event) => {
        if (!this.contains(event.target)) this.closeFields();
      }, { signal: this.controller.signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.container, { childList: true });
      this.hidden = false;
      this.schedule();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      state.components.delete(this);
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        updateUrl(false);
        this.apply();
      });
    }

    label(key, value) {
      if (key !== 'color') return value;
      return {
        'White/Black': this.dataset.whiteBlack,
        'Black/White': this.dataset.blackWhite,
        'Black/Orange': this.dataset.blackOrange
      }[value] || value;
    }

    closeFields(except) {
      for (const field of Object.values(this.fields)) {
        if (field !== except) field.open = false;
      }
    }

    onKeydown(event) {
      const field = event.target.closest('[data-li-ux-field]');
      if (!field) return;
      const summary = field.querySelector('summary');
      if (event.key === 'Escape' && field.open) {
        event.preventDefault();
        event.stopPropagation();
        field.open = false;
        summary.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const buttons = Array.from(field.querySelectorAll('[data-li-ux-option]:not(:disabled)'));
      if (!buttons.length) return;
      if (event.target === summary) {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        field.open = true;
        this.closeFields(field);
        const selected = buttons.find(button => button.getAttribute('aria-pressed') === 'true');
        (selected || buttons[event.key === 'ArrowUp' ? buttons.length - 1 : 0]).focus();
        return;
      }
      const index = buttons.indexOf(event.target);
      if (index === -1) return;
      event.preventDefault();
      let next = event.key === 'ArrowDown' ? (index + 1) % buttons.length : (index - 1 + buttons.length) % buttons.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = buttons.length - 1;
      buttons[next].focus();
    }

    optionButton(key, value, count) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'li-ux-collection-filters__option';
      option.dataset.liUxOption = value;
      option.setAttribute('aria-pressed', String(value === state.selection[key]));
      option.disabled = count === 0 && value !== state.selection[key];
      const swatches = { 'White/Black': ['#fff', '#141b24'], 'Black/White': ['#141b24', '#fff'], 'Black/Orange': ['#141b24', '#ff7800'] };
      if (key === 'color' && swatches[value]) {
        const swatch = document.createElement('span');
        swatch.className = 'li-ux-collection-filters__swatch';
        swatch.setAttribute('aria-hidden', 'true');
        for (const color of swatches[value]) {
          const circle = document.createElement('span');
          circle.style.setProperty('--swatch-color', color);
          swatch.append(circle);
        }
        option.append(swatch);
      }
      const label = document.createElement('span');
      const colorParts = key === 'color' && value ? this.label(key, value).split('/').map(part => part.trim()) : [];
      if (colorParts.length === 2 && colorParts.every(Boolean)) {
        label.className = 'li-ux-collection-filters__color-copy';
        for (const [index, name] of [this.dataset.shirtLabel, this.dataset.printLabel].entries()) {
          const line = document.createElement('span');
          line.textContent = name + ': ' + colorParts[index];
          label.append(line);
        }
      } else {
        label.textContent = value ? this.label(key, value) : this.fields[key].dataset.liUxPlaceholder;
      }
      const amount = document.createElement('span');
      amount.className = 'li-ux-collection-filters__option-count';
      amount.textContent = '(' + count + ')';
      const check = document.createElement('span');
      check.className = 'li-ux-collection-filters__option-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      option.append(label, amount, check);
      return option;
    }

    apply() {
      const context = contextFromUrl(window.location.href);
      let count = 0;
      for (const product of this.products) {
        const matches = product.variants.filter(variant => variantMatches(variant, state.selection, context));
        product.element.hidden = matches.length === 0;
        if (matches.length) count += 1;
        const chosen = matches.find(variant => variant.available) || matches[0];
        for (const link of product.element.querySelectorAll('a[href*="/products/"]')) {
          if (!link.dataset.liUxOriginalHref) link.dataset.liUxOriginalHref = link.getAttribute('href');
          if (chosen && (state.selection.size || state.selection.color)) {
            const url = new URL(link.dataset.liUxOriginalHref, window.location.href);
            if (url.origin !== window.location.origin) continue;
            url.searchParams.set('variant', chosen.id);
            link.setAttribute('href', url.pathname + url.search + url.hash);
          } else {
            link.setAttribute('href', link.dataset.liUxOriginalHref);
          }
        }
      }
      for (const key of ['size', 'color']) {
        const field = this.fields[key];
        const list = field.querySelector('[data-li-ux-options]');
        const values = ['', ...this.options[key]];
        if (state.selection[key] && !values.includes(state.selection[key])) values.push(state.selection[key]);
        const buttons = [];
        for (const value of values) {
          const candidate = Object.assign({}, state.selection, { [key]: value });
          const matchingCount = this.products.filter(product => product.variants.some(variant => variantMatches(variant, candidate, context))).length;
          buttons.push(this.optionButton(key, value, matchingCount));
        }
        list.replaceChildren(...buttons);
        field.querySelector('[data-li-ux-value]').textContent = state.selection[key] ? this.label(key, state.selection[key]) : field.dataset.liUxPlaceholder;
        field.classList.toggle('has-value', Boolean(state.selection[key]));
      }
      const text = (count === 1 ? this.dataset.countOne : this.dataset.countOther).replace('__count__', String(count));
      const countElement = this.querySelector('[data-li-ux-count]');
      if (countElement) countElement.textContent = text;
      for (const element of document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count')) element.textContent = text;
      this.querySelector('[data-li-ux-empty]').hidden = count !== 0;
      this.querySelector('[data-li-ux-reset]').hidden = !state.selection.size && !state.selection.color;
      this.dataset.visibleCount = String(count);
      this.dataset.liUxReady = '';
    }
  }

  window.addEventListener('popstate', () => {
    state.selection = selectionFromUrl(window.location.href);
    for (const component of state.components) component.schedule();
  });
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('facet-remove a.active-facets__button-remove')) {
      state.selection = { size: '', color: '' };
      for (const component of state.components) component.schedule();
    }
  }, true);
  customElements.define('li-ux-collection-filters', CollectionFilters);
})();
